import { FC, PropsWithChildren, ReactNode, memo, useState } from 'react';

import clsx from 'clsx';
import { get } from 'lodash';
import { TbChevronDown } from 'react-icons/tb';

import {
  Text,
  Box,
  Grid,
  GridProps,
  Chip,
  Group,
  Accordion,
  Button,
  Divider,
  AccordionProps,
  Skeleton,
  TextProps
} from '@ui';

import summaryClasses from '../adaptive-form/adaptive-form-summary/BasicFieldSummary.module.css';

import classes from './styles/Summary.module.css';

interface SummarySectionProps extends PropsWithChildren {
  // title of the section
  title?: string;
  // custom header rendered inside the accordion control instead of title
  header?: ReactNode;
  // function to call when edit button is clicked
  onEdit?: () => void;
  // if true, edit button will be disabled
  disableEdit?: boolean;
  /**
   * Additional props for the accordion component.
   * Useful for customizing the accordion behavior.
   */
  accordionProps?: AccordionProps;
  /**
   * If true, the control will not be rendered.
   * Useful for sections that do not require a control.
   * Title will be shown within panel.
   */
  withoutControl?: boolean;
  // type of summary section member -> for merged member summary
  type?: 'default' | 'member';
}

interface SummaryItemProps extends PropsWithChildren {
  capitalize?: boolean;
  inline?: boolean;
  loading?: boolean;
}

interface SummarySkeletonProps {
  size?: 'small' | 'medium';
}

const SummarySectionTitle: FC<PropsWithChildren> = memo(({ children }) => {
  return (
    <Text size="sm" component="h5" ta="left" c="dimmed" fw={600}>
      {children}
    </Text>
  );
});

const SummaryItemTitle: FC<PropsWithChildren & TextProps> = memo(
  ({ children, ...props }) => {
    return (
      <Text lightColor="dimmed" darkColor="white.5" size="md" {...props}>
        {children}
      </Text>
    );
  }
);

const SummaryItem: FC<SummaryItemProps & TextProps> = memo(
  ({ capitalize, inline, children, loading, ...props }) => {
    return !loading ? (
      <Text
        lightColor="dark"
        size="sm"
        display={inline ? 'inline' : 'block'}
        style={{ wordWrap: 'break-word' }}
        component={inline ? 'span' : 'p'}
        fw={500}
        tt={capitalize ? 'capitalize' : undefined}
        {...props}
      >
        {children}
      </Text>
    ) : (
      <Skeleton />
    );
  }
);

const SummarySection: FC<SummarySectionProps> = memo(
  ({
    title,
    accordionProps,
    header,
    onEdit,
    disableEdit,
    children,
    withoutControl,
    type = 'default'
  }) => {
    const sectionKey = `summary-section-${title}`;
    const [_value, setValue] = useState<string | null>(sectionKey);

    const handleChange = (value: string | null) => {
      setValue(value);
    };

    return (
      <Accordion
        value={_value}
        onChange={handleChange}
        chevron={<TbChevronDown size={24} />}
        classNames={{
          root: classes.root,
          control: classes.control,
          label: classes.label,
          item: clsx(classes.item, { [classes.itemExtended]: type === 'member' }),
          content: classes.content,
          chevron: classes.chevron
        }}
        w="100%"
        {...accordionProps}
      >
        <Accordion.Item value={sectionKey} mod={{ wc: withoutControl }}>
          {!withoutControl && (
            <Accordion.Control>
              {!header ? (
                <Group justify="space-between" wrap="nowrap" align="center">
                  <Text fz={{ base: 14, sm: 20 }} fw={600}>
                    {title}
                  </Text>
                  <Group justify="flex-end" gap="md" wrap="nowrap" align="center">
                    {onEdit && (
                      <Button
                        variant="outline"
                        color="secondary"
                        onClick={onEdit}
                        disabled={disableEdit}
                        size="lg"
                        component="span"
                      >
                        Edit
                      </Button>
                    )}
                    <Divider orientation="vertical" mr="md" />
                  </Group>
                </Group>
              ) : (
                header
              )}
            </Accordion.Control>
          )}
          <Accordion.Panel pt={!withoutControl ? undefined : 8}>
            {withoutControl && (
              <Text component="p" fz={{ base: 14, sm: 18 }} fw={600} mb="md">
                {title}
              </Text>
            )}
            <Box className={summaryClasses.gridContainer}>{children}</Box>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    );
  }
);

const SummarySkeleton: FC<SummarySkeletonProps> = memo(({ size }) => {
  if (size === 'small') return <Box className={classes.skeleton} w={50} />;
  if (size === 'medium') return <Box className={classes.skeleton} w={100} />;
  return <Box className={classes.skeleton} w={200} />;
});

interface SummaryMemberBoxProps extends PropsWithChildren {
  index: number;
  totalMembers: number;
}

const SummaryMemberBox: FC<SummaryMemberBoxProps> = memo(
  ({ index, totalMembers, children }) => {
    const rows = Math.ceil(totalMembers / 3);
    return (
      <Grid.Col
        key={index}
        span={{ xs: 12, sm: 4 }}
        className={clsx(
          (index + 1) % 3 !== 0 ? classes.memberBorderRight : null,
          rows > 1 && index < rows * 3 - 3 ? classes.memberBorderBottom : null,
          index % 3 === 0 ? classes.memberJustifyStart : classes.memberJustifyCenter,
          index < totalMembers - 1 ? classes.memberBorderBottomXs : null
        )}
      >
        {children}
      </Grid.Col>
    );
  }
);

export const NormalizeSummaryItemValue = (value: any, fallbackValue?: any) => {
  if (!value && fallbackValue) return fallbackValue;

  if (typeof value === 'boolean') {
    return value ? 'YES' : 'NO';
  }
  return value;
};

export interface SummaryItemFieldConfig {
  name: string;
  label: string | ReactNode;
  // use this to take a whole row. Useful for subtitles.
  fullRow?: boolean;
  // the value is not rendered for a subtitle field.
  isSubtitle?: boolean;
  // size of the field, defaults to 12
  gridProps?: GridProps;
  // use for falsy value
  fallbackValue?: any;
}

interface SummaryItemFieldProps<T> {
  data: T;
  fieldConfig: SummaryItemFieldConfig;
  loading?: boolean;
}

const SummaryItemField = <T,>({
  data,
  fieldConfig,
  loading
}: SummaryItemFieldProps<T>) => {
  const { name, label, fullRow, isSubtitle, fallbackValue } = fieldConfig;
  const longLabel = typeof label === 'string' && label.length > 55;
  if (!label) return null;

  return (
    <Grid.Col span={{ xs: 12, sm: fullRow || longLabel ? 12 : 4 }} key={name}>
      {isSubtitle ? (
        <SummaryItem>{label}</SummaryItem>
      ) : (
        <>
          <SummaryItemTitle>{label}</SummaryItemTitle>
          <SummaryItem loading={loading}>
            {NormalizeSummaryItemValue(get(data, name), fallbackValue)}
          </SummaryItem>
        </>
      )}
    </Grid.Col>
  );
};

type SummarySectionWithSubQuestionsProps<T> = {
  data: T;
  config: Record<string, SummaryItemFieldConfig>;
};

const SummarySectionWithSubQuestions = <T,>({
  data,
  config
}: SummarySectionWithSubQuestionsProps<T>) => {
  if (!get(data, config.answer.name) && config.answer.isSubtitle) {
    return null;
  }

  if (!get(data, config.answer.name)) {
    return (
      <SummaryItemField<T>
        key={config.answer.name}
        data={data}
        fieldConfig={config.answer}
      />
    );
  }
  return (
    <>
      {Object.values(config).map((value) => (
        <SummaryItemField<T> key={value.name} data={data} fieldConfig={value} />
      ))}
    </>
  );
};

interface SummarySectionForArrayField<T> {
  data: T;
  config: SummaryItemFieldConfig & {
    dataPath: string;
    subFieldTitle?: string;
  };
  elementConfig?: Record<string, SummaryItemFieldConfig>;
  dynamicElementConfig?: (
    value: any,
    config: Record<string, SummaryItemFieldConfig>
  ) => Record<string, SummaryItemFieldConfig>;
}

const SummarySectionForArrayField = <T,>({
  data,
  elementConfig,
  config,
  dynamicElementConfig
}: SummarySectionForArrayField<T>) => {
  if (!get(data, config.name)) {
    return (
      <SummaryItemField<T> key={config.name} data={data} fieldConfig={config} />
    );
  }

  const arrayData = get(data, config.dataPath);

  if (
    Array.isArray(arrayData) &&
    arrayData.length &&
    typeof arrayData[0] === 'string'
  ) {
    return (
      <>
        <SummaryItemField<T> key={config.name} data={data} fieldConfig={config} />
        <Grid.Col span={12}>
          <Grid gutter="sm">
            {arrayData.map((a: string) => (
              <Grid.Col key={a}>
                <Chip variant="outline">{a}</Chip>
              </Grid.Col>
            ))}
          </Grid>
        </Grid.Col>
      </>
    );
  }

  const rows = [
    <SummaryItemField<T> key={config.name} data={data} fieldConfig={config} />
  ];

  if (Array.isArray(arrayData) && arrayData.length) {
    arrayData.forEach((elementData, index) => {
      rows.push(
        <SummaryItemField
          // @ts-expect-error rendering a title
          fieldConfig={{
            label: `${config.subFieldTitle} - ${index + 1}`,
            isSubtitle: true,
            fullRow: true
          }}
        />
      );

      if (elementConfig) {
        let updatedElementConfig = elementConfig;
        if (typeof dynamicElementConfig === 'function') {
          updatedElementConfig = dynamicElementConfig(elementData, elementConfig);
        }

        rows.push(
          <>
            {Object.values(updatedElementConfig).map((value) => {
              return (
                <SummaryItemField<T>
                  key={value.name}
                  data={elementData}
                  fieldConfig={value}
                />
              );
            })}
          </>
        );
      }
    });

    return <>{rows}</>;
  }

  return null;
};

export {
  SummarySection,
  SummarySectionTitle,
  SummaryItemTitle,
  SummaryItem,
  SummarySkeleton,
  SummaryMemberBox,
  SummaryItemField,
  SummarySectionWithSubQuestions,
  SummarySectionForArrayField
};
